import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  fetchStepperData,
  getPortalDataIhx,
} from "../../../models/preAuthSlice";
import {
  DropdownOption,
  dropdownService,
  getPreAuthIhx,
} from "../../../apis/PreAuthService";
import PreAuthIhx from "../../../components/patient/PreAuth/PreAuthIhx/PreAuthIhx";
import Loader from "../../../utils/Spinners";
import { getBasicDetails, getTimelineDetails } from "../../../models/patientSlice";

type PreAuthIhxContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const PreAuthIhxContainer: FC<PreAuthIhxContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [diagnosisOptions, setDiagnosisOptions] = useState<DropdownOption[]>(
    []
  );
  const [proposedLineOfTreatment, setProposedLineOfTreatment] = useState<any>();
  const [roomTypeOptions, setRoomTypeOptions] = useState<DropdownOption[]>([]);
  const [generalRoomTypeOptions, setGeneralRoomTypeOptions] = useState<
    DropdownOption[]
  >([]);

  const [treatmentOptions, setTreatmentOptions] = useState<DropdownOption[]>(
    []
  );
  const [procedureOptions, setProcedureOptions] = useState<DropdownOption[]>(
    []
  );
  const [preAuthData, setPreAuthData] = useState<any>(null);
  const [isFetchingDropdownData, setIsFetchingDropdownData] =
    useState<boolean>(false);
  const [loading, setLoading] = useState({
    diagnosis: true,
    roomType: true,
    procedure: true,
    preAuth: true,
  });
  const { hospital, patients } = useSelector((state: RootState) => state);
  const [error, setError] = useState({
    diagnosis: null as string | null,
    roomType: null as string | null,
    procedure: null as string | null,
    preAuth: null as any,
  });

  const fetchStepperForStatus = () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  function extractKeysFromApiResponse(apiResponse: any) {
    let extractedKeys = [];
    for (let lineOfTreatment in apiResponse) {
      const [label, value] = lineOfTreatment.split("::");
      extractedKeys.push({ label, value: lineOfTreatment });
    }

    return extractedKeys;
  }

  const fetchDiagnosis = async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, diagnosis: true }));
      setError((prev) => ({ ...prev, diagnosis: null }));
      if (
        patients?.fetchBasicDetails?.casesResponse?.healthCareId &&
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
      ) {
        const options = await dropdownService.fetchDiagnosisOptions(
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
        );
        setDiagnosisOptions(options);
      }
    } catch (err) {
      setError((prev) => ({
        ...prev,
        diagnosis:
          err instanceof Error ? err.message : "Failed to fetch diagnosis data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, diagnosis: false }));
    }
  };

  // Fetch room type data
  const fetchRoomType = React.useCallback(async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, roomType: true }));
      setError((prev) => ({ ...prev, roomType: null }));
      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const options = await dropdownService.fetchRoomTypeOptions(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
        );
        setRoomTypeOptions(options?.roomTypes);
        setGeneralRoomTypeOptions(options?.generalRoomTypes);
      }
    } catch (err) {
      setError((prev) => ({
        ...prev,
        roomType:
          err instanceof Error ? err.message : "Failed to fetch room type data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, roomType: false }));
    }
    }, [patients?.fetchBasicDetails?.casesResponse]);

  // Fetch procedure data
  const fetchProcedure = React.useCallback(async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, procedure: true }));
      setError((prev) => ({ ...prev, procedure: null }));

      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const options = await dropdownService.fetchProcedureOptions(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
        );
        setProcedureOptions(options);
      }
    } catch (err) {
      setError((prev) => ({
        ...prev,
        procedure:
          err instanceof Error ? err.message : "Failed to fetch procedure data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, procedure: false }));
    }
  }, [patients?.fetchBasicDetails?.casesResponse]);

  const fetchTreatment = async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, treatment: true }));
      setError((prev) => ({ ...prev, treatment: null }));
      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName) {
        const options = await dropdownService.fetchTreatmentOptions(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0]
        );
        setProposedLineOfTreatment(extractKeysFromApiResponse(options));
        setTreatmentOptions(options);
      }
    } catch (err) {
      setError((prev) => ({
        ...prev,
        procedure:
          err instanceof Error ? err.message : "Failed to fetch procedure data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, procedure: false }));
    }
  };

  const fetchPreAuthData = async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, preAuth: true }));
      setError((prev) => ({ ...prev, preAuth: null }));

      const data = await getPreAuthIhx(
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id
      );
      if (
        patients?.fetchBasicDetails?.casesResponse?.id &&
        patients?.fetchBasicDetails?.visitInfoResponse?.id
      ) {
        dispatch(
          getPortalDataIhx({
            caseId: patients?.fetchBasicDetails?.casesResponse?.id,
            visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          })
        );
      }
      setPreAuthData(data);
    } catch (err) {
      setError((prev) => ({
        ...prev,
        preAuth:
          err instanceof Error ? err.message : "Failed to fetch pre Auth data",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, preAuth: false }));
    }
  };

  // Fetch all data
  const fetchAll = async (): Promise<void> => {
    try {
      setIsFetchingDropdownData(true);
      await Promise.all([
        fetchDiagnosis(),
        fetchRoomType(),
        fetchProcedure(),
        fetchTreatment(),
        fetchPreAuthData(),
      ]);
      setIsFetchingDropdownData(false);
    } catch (err) {
      setIsFetchingDropdownData(false);
    }
  };

  //   useEffect(() => {
  //     fetchPreAuthData();
  //   }, [patients?.fetchBasicDetails?.casesResponse?.id,
  //         patients?.fetchBasicDetails?.visitInfoResponse?.id,])

  // Fetch data on component mount
  useEffect(() => {
    if (activeKey && activeKey?.length > 0) fetchAll();
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        })
      );
    }
  };

  useEffect(() => {
    if (activeKey && activeKey?.length > 0) {
      // fetchStepperForStatus();
      // getTimelineDetail();
    }
  }, [
    preAuthData,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  return (
    <div>
      {" "}
      {isFetchingDropdownData ? (
        <Loader />
      ) : (
        <PreAuthIhx
          diagnosisOptions={diagnosisOptions}
          roomTypeOptions={roomTypeOptions}
          generalRoomTypeOptions={generalRoomTypeOptions}
          procedureOptions={procedureOptions}
          treatmentOptions={treatmentOptions}
          preAuthData={preAuthData}
          fetchPreAuthData={fetchPreAuthData}
          proposedLineOfTreatment={proposedLineOfTreatment}
          fetchStepperForStatus={fetchStepperForStatus}
          getTimelineDetail={getTimelineDetail}
          activeKey={activeKey}
          sseData={sseData}
          sseUpdateCount={sseUpdateCount}
        />
      )}
    </div>
  );
};

export default React.memo(PreAuthIhxContainer);
